import { useState } from 'react'
import { format } from 'date-fns'
import { User, Users, Plus, KeyRound, UserCheck, UserX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { useAuthStore } from '@/stores/authStore'
import { useCurrentUser } from '@/hooks/useAuth'
import {
  useUsers,
  useCreateUser,
  useDeactivateUser,
  useActivateUser,
  useResetPassword,
} from '@/hooks/useUsers'
import { UserRole, UserCreate, UserResponse } from '@/types/api'

export function SettingsPage() {
  const { user } = useAuthStore()
  const { isLoading: userLoading } = useCurrentUser()
  const isAdmin = user?.role === UserRole.ADMIN

  const { data: users, isLoading: usersLoading } = useUsers({
    include_inactive: true,
  })

  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false)
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null)
  const [resetPasswordValue, setResetPasswordValue] = useState('')
  const [createFormData, setCreateFormData] = useState<UserCreate>({
    username: '',
    email: '',
    password: '',
    role: UserRole.USER,
  })

  const createUserMutation = useCreateUser()
  const deactivateMutation = useDeactivateUser()
  const activateMutation = useActivateUser()
  const resetPasswordMutation = useResetPassword()

  if (userLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  const handleCreateUser = () => {
    createUserMutation.mutate(createFormData, {
      onSuccess: () => {
        setShowCreateDialog(false)
        setCreateFormData({ username: '', email: '', password: '', role: UserRole.USER })
      },
    })
  }

  const handleResetPassword = () => {
    if (resetPasswordUserId && resetPasswordValue) {
      resetPasswordMutation.mutate(
        { id: resetPasswordUserId, password: resetPasswordValue },
        {
          onSuccess: () => {
            setShowResetPasswordDialog(false)
            setResetPasswordUserId(null)
            setResetPasswordValue('')
          },
        }
      )
    }
  }

  const openResetPasswordDialog = (userId: string) => {
    setResetPasswordUserId(userId)
    setResetPasswordValue('')
    setShowResetPasswordDialog(true)
  }

  const handleToggleActive = (userRecord: UserResponse) => {
    if (userRecord.is_active) {
      deactivateMutation.mutate(userRecord.id)
    } else {
      activateMutation.mutate(userRecord.id)
    }
  }

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">
          Manage your profile and system settings
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-3">
        <TabsList>
          <TabsTrigger value="profile">
            <User className="mr-2 h-4 w-4" />
            Profile
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="users">
              <Users className="mr-2 h-4 w-4" />
              Users
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                View your account details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Username</Label>
                  <Input value={user?.username || ''} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Input
                    value={user?.role === UserRole.ADMIN ? 'Administrator' : 'User'}
                    disabled
                  />
                </div>
                <div className="space-y-2">
                  <Label>Member Since</Label>
                  <Input
                    value={
                      user?.created_at
                        ? format(new Date(user.created_at), 'MMMM d, yyyy')
                        : ''
                    }
                    disabled
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users Tab (Admin only) */}
        {isAdmin && (
          <TabsContent value="users">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>User Management</CardTitle>
                  <CardDescription>
                    Manage system users and their access
                  </CardDescription>
                </div>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add User
                </Button>
              </CardHeader>
              <CardContent>
                {usersLoading ? (
                  <div className="flex h-32 items-center justify-center">
                    <Spinner />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Username</TableHead>
                        <TableHead className="hidden md:table-cell">Email</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="hidden md:table-cell">Status</TableHead>
                        <TableHead className="hidden md:table-cell">Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {users?.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">
                            {u.username}
                            {u.id === user?.id && (
                              <Badge variant="outline" className="ml-2">
                                You
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">{u.email}</TableCell>
                          <TableCell className="capitalize">{u.role}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge
                              variant={u.is_active ? 'success' : 'secondary'}
                            >
                              {u.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-muted-foreground">
                            {format(new Date(u.created_at), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openResetPasswordDialog(u.id)}
                                disabled={resetPasswordMutation.isPending}
                              >
                                <KeyRound className="h-4 w-4" />
                              </Button>
                              {u.id !== user?.id && (
                                <Button
                                  variant={u.is_active ? 'destructive' : 'default'}
                                  size="sm"
                                  onClick={() => handleToggleActive(u)}
                                  disabled={
                                    deactivateMutation.isPending ||
                                    activateMutation.isPending
                                  }
                                >
                                  {u.is_active ? (
                                    <UserX className="h-4 w-4" />
                                  ) : (
                                    <UserCheck className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system. Set a username, email, and password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={createFormData.username}
                onChange={(e) =>
                  setCreateFormData((prev) => ({
                    ...prev,
                    username: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={createFormData.email}
                onChange={(e) =>
                  setCreateFormData((prev) => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={createFormData.password}
                onChange={(e) =>
                  setCreateFormData((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select
                value={createFormData.role}
                onValueChange={(v) =>
                  setCreateFormData((prev) => ({
                    ...prev,
                    role: v as UserRole,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UserRole.USER}>User</SelectItem>
                  <SelectItem value={UserRole.ADMIN}>Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={
                createUserMutation.isPending ||
                !createFormData.username ||
                !createFormData.email ||
                !createFormData.password
              }
            >
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter a new password for this user.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reset-password">New Password</Label>
            <Input
              id="reset-password"
              type="password"
              value={resetPasswordValue}
              onChange={(e) => setResetPasswordValue(e.target.value)}
              placeholder="Enter new password"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowResetPasswordDialog(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPasswordMutation.isPending || !resetPasswordValue || resetPasswordValue.length < 4}
            >
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
